import type * as React from 'react';

import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ErrorPaneProps {
  title?: string;
  message: string;
  // Raw technical text (an OIDC/HTTP error string). Kept for support, but
  // DEMOTED to a small secondary line — it must never be the headline a
  // dispatcher reads.
  detail?: string | null;
  onRetry?: () => void;
}

// Centered error display used by AuthGate when the OIDC bootstrap throws.
// Uses MUI Alert in `severity="error"` for the right semantic palette.
//
// Layout contract (2026-08-24): the title is its own `AlertTitle` line, NOT
// glued to the message with a `": "` separator. The old glue produced
// "…selhalo.: Token is not active" whenever the title ended in a full stop, and
// it put a raw English technical string in the primary reading position.
// `title` + `message` keep their meaning for every existing caller.
export const ErrorPane = ({
  title,
  message,
  detail,
  onRetry,
}: ErrorPaneProps): React.JSX.Element => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100vw',
        height: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Stack spacing={2} alignItems="stretch" sx={{ maxWidth: 480, width: '100%' }}>
        <Alert severity="error">
          {title ? <AlertTitle>{title}</AlertTitle> : null}
          {message}
          {detail ? (
            <Typography
              variant="caption"
              component="p"
              sx={{ mt: 1, color: 'text.secondary', wordBreak: 'break-word' }}
            >
              {t('error.technicalDetail', { detail })}
            </Typography>
          ) : null}
        </Alert>
        {onRetry ? (
          <Button variant="contained" color="primary" onClick={onRetry}>
            {t('retry')}
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
};
